import "./markdown-field.scss";
import { TaskItem, TaskList } from "@tiptap/extension-list";
import { Table, TableCell, TableHeader, TableRow } from "@tiptap/extension-table";
import { Placeholder } from "@tiptap/extensions";
import { Editor, EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import React, { useEffect, useRef, useState } from "react";
import { Markdown } from "tiptap-markdown";
import { BrandAutocorrect } from "./brand-autocorrect";
import { promptDialog } from "./confirm-service";
import { DashTaskList } from "./dash-task-list";
import EditorLinkMenu from "./editor-link-menu";
import EmojiPicker from "./emoji-picker";
import { IssueKeyDecoration } from "./issue-key-decoration";
import { MentionDecoration } from "./mention-decoration";
import { useMentionSuggest } from "./mention-suggest";

export interface MarkdownFieldProps {
  value: string;
  placeholder?: string;
  onChange: (next: string) => void;
  minHeight?: number;
  // Kept for API compatibility; the empty state is now the editor placeholder.
  emptyHint?: React.ReactNode;
  // When provided, bare issue keys (KUI-12) with a real project prefix are chipped (decoration only,
  // the stored Markdown is untouched) and a click invokes onIssueKey.
  issueKeys?: Set<string>;
  onIssueKey?: (key: string) => void;
  // Member display names for @-mention autocomplete (same as the comment editor). Omitted = no
  // mentions (e.g. create mode before members load).
  mentionCandidates?: string[];
}

// WYSIWYG editor that reads and writes Markdown (Jira/Linear-style): typing Markdown shortcuts
// (**bold**, `# `, `- `, `> `, ``` ``` ```, `1. `) formats inline, the toolbar covers the rest
// (incl. tables and an emoji picker), and the document is serialized back to Markdown (GFM, so
// tables survive) for storage. Power users can write raw Markdown; everyone else gets rich
// editing.

interface ToolbarButtonProps {
  label: React.ReactNode;
  title: string;
  active?: boolean;
  onRun: () => void;
}

const ToolbarButton: React.FC<ToolbarButtonProps> = ({ label, title, active, onRun }) => (
  <button
    type="button"
    className={`kuika-md-editor__tool${active ? " kuika-md-editor__tool--active" : ""}`}
    title={title}
    aria-label={title}
    aria-pressed={active}
    // Keep the editor's selection while clicking the toolbar (so the command applies to it).
    onMouseDown={(event: React.MouseEvent) => event.preventDefault()}
    onClick={onRun}
  >
    {label}
  </button>
);

interface ToolbarProps {
  editor: Editor;
  // Whether the editor is focused. Active states (bold/list/…) reflect the caret's marks, which
  // persist on blur — so only surface them while editing, else a blurred editor shows buttons lit
  // based on wherever the caret was last left (e.g. the document end).
  focused: boolean;
  onToggleEmoji: () => void;
  emojiOpen: boolean;
}

const Toolbar: React.FC<ToolbarProps> = ({ editor, focused, onToggleEmoji, emojiOpen }) => {
  const activeFor = (name: string, level?: number): boolean =>
    focused && (level === undefined ? editor.isActive(name) : editor.isActive(name, { level }));

  const setLink = (): void => {
    const previous: string = (editor.getAttributes("link").href as string) ?? "";
    void promptDialog({
      title: "Link",
      label: "URL",
      placeholder: "https://",
      defaultValue: previous,
      confirmLabel: "Apply",
      allowEmpty: true,
      intent: "create",
      iconName: "link"
    }).then((url: string | null) => {
      if (url === null) return;
      if (url === "") {
        editor.chain().focus().extendMarkRange("link").unsetLink().run();
        return;
      }
      editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
    });
  };

  const inTable: boolean = focused && editor.isActive("table");

  return (
    <div className="kuika-md-editor__toolbar">
      <ToolbarButton
        label={<strong>B</strong>}
        title="Bold  (⌘B)"
        active={activeFor("bold")}
        onRun={() => editor.chain().focus().toggleBold().run()}
      />
      <ToolbarButton
        label={<em>I</em>}
        title="Italic  (⌘I)"
        active={activeFor("italic")}
        onRun={() => editor.chain().focus().toggleItalic().run()}
      />
      <ToolbarButton
        label={<s>S</s>}
        title="Strikethrough"
        active={activeFor("strike")}
        onRun={() => editor.chain().focus().toggleStrike().run()}
      />
      <ToolbarButton
        label={<>&lt;/&gt;</>}
        title="Inline code"
        active={activeFor("code")}
        onRun={() => editor.chain().focus().toggleCode().run()}
      />
      <span className="kuika-md-editor__tool-sep" />
      <ToolbarButton
        label="H1"
        title="Heading 1"
        active={activeFor("heading", 1)}
        onRun={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
      />
      <ToolbarButton
        label="H2"
        title="Heading 2"
        active={activeFor("heading", 2)}
        onRun={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
      />
      <ToolbarButton
        label="H3"
        title="Heading 3"
        active={activeFor("heading", 3)}
        onRun={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
      />
      <span className="kuika-md-editor__tool-sep" />
      <ToolbarButton
        label="•"
        title="Bullet list"
        active={activeFor("bulletList")}
        onRun={() => editor.chain().focus().toggleBulletList().run()}
      />
      <ToolbarButton
        label="1."
        title="Numbered list"
        active={activeFor("orderedList")}
        onRun={() => editor.chain().focus().toggleOrderedList().run()}
      />
      <ToolbarButton
        label={
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <rect x="2.25" y="3" width="4.5" height="4.5" rx="1.25" stroke="currentColor" strokeWidth="1.2" />
            <path
              d="M3.4 5.25l1 1 1.4-1.7"
              stroke="currentColor"
              strokeWidth="1.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <rect x="2.25" y="8.75" width="4.5" height="4.5" rx="1.25" stroke="currentColor" strokeWidth="1.2" />
            <path d="M9 5h5M9 11h5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
          </svg>
        }
        title="Checklist"
        active={activeFor("taskList")}
        onRun={() => editor.chain().focus().toggleTaskList().run()}
      />
      <ToolbarButton
        label="❝"
        title="Quote"
        active={activeFor("blockquote")}
        onRun={() => editor.chain().focus().toggleBlockquote().run()}
      />
      <ToolbarButton
        label={<>{"{ }"}</>}
        title="Code block"
        active={activeFor("codeBlock")}
        onRun={() => editor.chain().focus().toggleCodeBlock().run()}
      />
      <span className="kuika-md-editor__tool-sep" />
      <ToolbarButton
        label={
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path
              d="M6.7 8.7a2.5 2.5 0 0 0 3.5 0l1-1a2.5 2.5 0 1 0-3.5-3.5l-.6.6"
              stroke="currentColor"
              strokeWidth="1.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M9.3 7.3a2.5 2.5 0 0 0-3.5 0l-1 1a2.5 2.5 0 1 0 3.5 3.5l.6-.6"
              stroke="currentColor"
              strokeWidth="1.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        }
        title="Link"
        active={activeFor("link")}
        onRun={setLink}
      />
      <ToolbarButton
        label={
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <circle cx="8" cy="8" r="5.75" stroke="currentColor" strokeWidth="1.2" />
            <circle cx="6" cy="6.9" r="0.85" fill="currentColor" />
            <circle cx="10" cy="6.9" r="0.85" fill="currentColor" />
            <path
              d="M5.5 9.6a3 3 0 0 0 5 0"
              stroke="currentColor"
              strokeWidth="1.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        }
        title="Emoji"
        active={emojiOpen}
        onRun={onToggleEmoji}
      />
      <ToolbarButton
        label={
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <rect x="2.25" y="3.25" width="11.5" height="9.5" rx="1.5" stroke="currentColor" strokeWidth="1.2" />
            <path d="M2.5 6.75h11M8 6.75V12.5" stroke="currentColor" strokeWidth="1.2" />
          </svg>
        }
        title="Insert table"
        onRun={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}
      />
      {inTable && (
        <>
          <span className="kuika-md-editor__tool-sep" />
          <ToolbarButton label="+Col" title="Add column" onRun={() => editor.chain().focus().addColumnAfter().run()} />
          <ToolbarButton label="−Col" title="Delete column" onRun={() => editor.chain().focus().deleteColumn().run()} />
          <ToolbarButton label="+Row" title="Add row" onRun={() => editor.chain().focus().addRowAfter().run()} />
          <ToolbarButton label="−Row" title="Delete row" onRun={() => editor.chain().focus().deleteRow().run()} />
          <ToolbarButton label="⌫" title="Delete table" onRun={() => editor.chain().focus().deleteTable().run()} />
        </>
      )}
    </div>
  );
};

const MarkdownField: React.FC<MarkdownFieldProps> = ({
  value,
  placeholder,
  onChange,
  minHeight = 80,
  issueKeys,
  onIssueKey,
  mentionCandidates
}) => {
  const mentions = useMentionSuggest(mentionCandidates ?? []);
  const [emojiOpen, setEmojiOpen] = useState<boolean>(false);
  const [focused, setFocused] = useState<boolean>(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  // Latest member names for the mention-chip decoration, read through a ref (they load async).
  const mentionNamesRef = useRef<string[]>(mentionCandidates ?? []);
  mentionNamesRef.current = mentionCandidates ?? [];
  // The last Markdown we read out / wrote in — so external `value` syncs don't fight local edits
  // and blur only persists on a real change.
  const lastMarkdown = useRef<string>(value);
  const onChangeRef = useRef<(next: string) => void>(onChange);
  onChangeRef.current = onChange;

  const editor: Editor | null = useEditor({
    extensions: [
      // Link ships inside StarterKit since tiptap 3, so it is configured here rather than
      // added as a second extension (two instances of one extension is an error).
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
        link: {
          // Plain click opens the link (Jira-style) — the default isAllowedUri still blocks
          // javascript:/data:, and target/rel below open it safely in a new tab.
          openOnClick: true,
          autolink: true,
          linkOnPaste: true,
          HTMLAttributes: { rel: "noopener noreferrer nofollow", target: "_blank" }
        }
      }),
      Placeholder.configure({ placeholder: placeholder ?? "Add a description…" }),
      Table.configure({ resizable: true }),
      TableRow,
      TableHeader,
      TableCell,
      // GFM task lists (`- [ ]` / `- [x]`). tiptap-markdown auto-serializes any node named
      // taskList/taskItem to GFM, so these round-trip to Markdown for storage. nested:true lets
      // checklists nest like bullet lists. DashTaskList makes `- [ ] ` (GFM muscle memory) convert a
      // bullet into a checklist, since StarterKit grabs `- ` before `[ ]` can be typed.
      TaskList,
      TaskItem.configure({ nested: true }),
      DashTaskList,
      // html:false keeps the document Markdown-pure (no raw HTML smuggled in); linkify turns
      // pasted/typed URLs into links.
      Markdown.configure({ html: false, linkify: true, transformPastedText: true, transformCopiedText: true }),
      // Chip real issue keys (decoration only — never alters the stored Markdown).
      IssueKeyDecoration.configure({ projectKeys: issueKeys ?? new Set<string>() }),
      // Chip "@Name" mentions for known members (decoration only — the Markdown stays plain "@Name").
      MentionDecoration.configure({ getMemberNames: () => mentionNamesRef.current }),
      // Brand-word auto-correct as you type: kuika → Kuika, jira → JIRA.
      BrandAutocorrect
    ],
    content: value,
    // tiptap 3 stopped re-rendering the React tree on every transaction. The toolbar reads
    // `editor.isActive(...)` during render for its pressed states (and to show the table
    // controls), so it must opt back in or the buttons freeze at their mount-time state.
    shouldRerenderOnTransaction: true,
    editorProps: {
      attributes: { class: "kuika-md-field__prose kuika-md-editor__content" },
      handleKeyDown: (_view, event: KeyboardEvent): boolean =>
        editorRef.current ? mentions.handleKeyDown(editorRef.current, event) : false
    },
    onUpdate: ({ editor: instance }) => mentions.sync(instance),
    onSelectionUpdate: ({ editor: instance }) => mentions.sync(instance),
    onFocus: () => setFocused(true),
    onBlur: ({ editor: instance }) => {
      setFocused(false);
      const markdown: string = instance.storage.markdown.getMarkdown();
      if (markdown !== lastMarkdown.current) {
        lastMarkdown.current = markdown;
        onChangeRef.current(markdown);
      }
    }
  });

  // Stable handle to the editor for the (creation-time) key-handler closure.
  const editorRef = useRef<Editor | null>(null);
  editorRef.current = editor;

  // Reflect external changes (issue switch, a teammate's edit) without clobbering an in-progress
  // edit: only re-seed when the field isn't focused and the Markdown actually differs.
  useEffect(() => {
    if (!editor) return;
    const current: string = editor.storage.markdown.getMarkdown();
    if (value !== current && !editor.isFocused) {
      // tiptap 3 takes an options object here; the bare `false` used to mean emitUpdate.
      editor.commands.setContent(value, { emitUpdate: false });
      lastMarkdown.current = value;
    }
  }, [value, editor]);

  // Member names load after the editor mounts; nudge the decoration to re-scan (an empty transaction
  // drives MentionDecoration's apply) so an already-typed "@Name" chips once the directory resolves.
  useEffect(() => {
    if (editor) editor.view.dispatch(editor.state.tr);
  }, [editor, mentionCandidates]);

  // Close the emoji popover on an outside click.
  useEffect(() => {
    if (!emojiOpen) return;
    const onDown = (event: MouseEvent): void => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) setEmojiOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [emojiOpen]);

  const insertEmoji = (emoji: string): void => {
    editor?.chain().focus().insertContent(emoji).run();
    setEmojiOpen(false);
  };

  // A click on a promoted issue-key chip opens that issue (the decoration carries the key).
  const handleChipClick = (event: React.MouseEvent): void => {
    if (!onIssueKey) return;
    const target: HTMLElement | null = event.target as HTMLElement | null;
    const chip: HTMLElement | null =
      (target?.closest?.(".kuika-board__issue-chip[data-issue-key]") as HTMLElement | null) ?? null;
    const key: string | null = chip?.getAttribute("data-issue-key") ?? null;
    if (key) {
      event.preventDefault();
      onIssueKey(key);
    }
  };

  return (
    <div className="kuika-md-editor" ref={wrapperRef} onClick={handleChipClick}>
      {editor && (
        <Toolbar
          editor={editor}
          focused={focused}
          emojiOpen={emojiOpen}
          onToggleEmoji={() => setEmojiOpen((open: boolean) => !open)}
        />
      )}
      {emojiOpen && (
        <div className="kuika-md-editor__emoji-pop">
          <EmojiPicker onPick={insertEmoji} />
        </div>
      )}
      <EditorContent editor={editor} className="kuika-md-editor__surface" style={{ minHeight }} />
      {mentions.popup(editor)}
      <EditorLinkMenu editor={editor} />
    </div>
  );
};

export default MarkdownField;
